import React, { FC, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import PageWithHeader from '@app/components/PageWithHeader';
import PageHeader from '@app/components/header/PageHeader';
import useLoadable from '@app/hooks/loadable/useLoadable';
import useInfraStores from '@app/hooks/useInfraStores';
import { UUID_REGEX } from '@app/utils/regexUtils';
import { Skeleton } from '@/components/ui/skeleton';
import MorteeMode from '@mortee/morteeMode';
import { getOrganizationSetup } from '@mortee/services/organizationSetupServices';
import OrganizationSetupCards, { OrganizationSetupCardsSkeleton } from '@mortee/routes/organizationSetup/OrganizationSetupCards';
import { LoadError, PageError } from '@mortee/routes/organizationSetup/OrganizationSetupErrors';

const OrganizationSetupPage: FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { navigationStore } = useInfraStores<MorteeMode>();
  const isValidOrganizationId = UUID_REGEX.test(organizationId);
  const [organizationSetup] = useLoadable(
    async () => (isValidOrganizationId ? getOrganizationSetup(organizationId) : null),
    [organizationId],
  );

  const renderTitle = (): ReactNode => {
    if (organizationSetup.result) {
      return organizationSetup.result.organizationName;
    }

    return organizationSetup.isNotSettled() ? <Skeleton className='h-7 w-64' /> : 'Organization Setup';
  };

  const renderContent = (): ReactNode => {
    if (!isValidOrganizationId) {
      return <PageError dataTestId='organization-setup-invalid-id'>This isn’t a valid organization id.</PageError>;
    }

    if (organizationSetup.isRejected()) {
      return (
        <LoadError
          error={organizationSetup.stateMetadata?.error}
          message='Cannot get the organization setup.'
          dataTestId='organization-setup-error'
        />
      );
    }

    if (!organizationSetup.result) {
      return <OrganizationSetupCardsSkeleton />;
    }

    return <OrganizationSetupCards organizationSetup={organizationSetup.result} />;
  };

  return (
    <PageWithHeader
      width='full'
      header={<PageHeader backButtonTo={navigationStore.generateOrganizationSetupListLink()}>{renderTitle()}</PageHeader>}
    >
      {renderContent()}
    </PageWithHeader>
  );
};

export default OrganizationSetupPage;
